import { Fragment, type ReactNode, useEffect, useState } from 'react'

import { useCurrentSession } from '~/hooks/useCurrentSession'
import { useServerTimeOffsetMs } from '~/hooks/useServerTimeOffsetMs'

/**
 * session RPC を待つ上限 (ms)。
 *
 * これを超えたら offset 未確定 (= ブラウザ時計) のまま描画へ進む。 サーバー時刻の
 * 取得は「表示と実行の乖離を防ぐ」ための手段であって、 取得できない時に画面を出さない
 * のは手段が目的を上回っている。 待ち切れなかった場合は従来どおりブラウザ時計で動く
 * (= この PR 以前の挙動) 方が害が小さい。
 *
 * 値は `docs/specs/non-functional-baseline.md`「画面初期表示 1 秒以内」に合わせる。
 * gate は本番の全ロードで route 描画の前に入るため、 gate 自身が非機能ベースラインを
 * 超える待ちを持ち込んではならない。 通常経路の実待ちは session RPC 1 往復 (数十 ms)
 * で、 この上限はサーバーが遅い / 無応答のときにだけ効く。
 */
const MAX_WAIT_MS = 1000

/**
 * 待ち上限経路で subtree を作り直す offset の下限 (ms)。
 *
 * offset は `server_time - レスポンス到着時のブラウザ時計` なので、 時計ズレも偽装も
 * 無い健全な環境でも片道遅延ぶん (数十〜数百 ms) は必ず非 0 になる。 「非 0 なら
 * 作り直す」 にすると回線が遅いだけの本番環境で毎回 remount し、 入力中のフォーム
 * state や `DemandForecastLegendProvider` の hiddenKeys
 * (`docs/specs/demand-forecast/demand-forecast-common.md` AC-DFC-009 は既定復帰の
 * 契機を「ブラウザ再読み込み時」 に限定している) を巻き込んで捨ててしまう。
 *
 * 作り直しで救えるのは「時刻依存の初期値」 で、 その最小粒度はコマ (30 分) と
 * 「今日」 (1 日)。 1 分を下限にすればこれらは取りこぼさず、 伝送遅延では発火しない。
 */
const REBUILD_THRESHOLD_MS = 60_000

type Props = {
  children: ReactNode
}

/**
 * ServerClockGate — サーバー時刻の offset が確定するまで children の初回描画を遅らせる。
 *
 * ## なぜ必要か
 *
 * ページ側には「今日」 をマウント時に 1 度だけ確定させる state がある
 * (`useState(() => now())` 由来の対象日など)。 offset は session RPC の完了時に
 * 確定するため、 gate が無いと初回描画がブラウザ時計で評価され、 偽装環境や利用者の
 * 時計ズレで「初期表示の日付だけサーバー基準とズレる」。 GC 判定はサーバー基準なので、
 * `docs/principles.md` 鉄則 1 の乖離になる。
 *
 * 個々のページで offset 確定後に初期値を再導出する手もあるが、 「ユーザーが既に選び直した
 * 日付を上書きしない」判断を各ページに分散させることになる。 「offset 確定後に時刻依存の
 * 初期値を評価する」 という不変条件をここ 1 箇所で満たす方が壊れにくい。
 *
 * ## 規約からの逸脱
 *
 * `I18nProvider` は「取得前後で描画をブロックしない」判断を採っている (i18n は
 * localStorage キャッシュで first-paint を成立させられるため)。 時刻には同等の
 * キャッシュが無く、 誤った初期日付は表示と実行の乖離に直結するため、 時計に限っては
 * 初回描画を待たせる。 待ちは session RPC の settle (成功・失敗どちらでも) か
 * [[MAX_WAIT_MS]] のどちらか早い方で解ける。
 *
 * ## 待ち上限で先に描画した場合
 *
 * [[MAX_WAIT_MS]] を超えて session RPC が成功すると、 children は既にブラウザ時計で
 * 初期値を確定させている。 そのままでは「offset は入っているのに初期日付だけブラウザ
 * 時計」という乖離が残るため、 offset が確定した時点で subtree を作り直して時刻依存の
 * 初期値を再評価させる。 ただし作り直すのは offset が [[REBUILD_THRESHOLD_MS]] 以上
 * (= 時計ズレ・偽装によって初期値が実際に変わる大きさ) のときだけで、 片道遅延ぶんの
 * 非 0 offset では作り直さない。 offset 確定後の定期補正 (TickerBar の
 * `refetchInterval` 由来の offset 変動) でも作り直さない — どちらも操作中の画面状態を
 * 捨てる方が害が大きいため。
 *
 * ## 2 機構を残している理由
 *
 * 「offset 確定後に時刻依存の初期値を評価する」 という不変条件は、 待ち (通常経路) と
 * 作り直し (待ち上限経路) のどちらか一方でも形式上は満たせる。 それでも両方持つのは、
 * 一方に寄せるとどちらも実挙動が悪化するため:
 *
 * - 作り直しだけに寄せる → 本番の全ロードで children が 2 回 mount され、 各ページの
 *   業務データ RPC も 2 回発火する
 * - 待ちだけに寄せる → サーバーが [[MAX_WAIT_MS]] 以内に返せない環境では、 偽装・
 *   時計ズレがあってもセッション中ずっと初期日付がブラウザ時計のまま残る
 *
 * 通常経路は待ちだけで解け (作り直しの key は最初から確定値)、 作り直しは待ち上限を
 * 超えた例外経路の救済に限定される、 という役割分担で両立させている。
 */
export const ServerClockGate = ({
  children,
}: Props): React.ReactElement | null => {
  // 起動時 1 回のみ。cache key は I18nProvider / TickerBar と同一なので RPC は共有され、
  // `server_time` の offset 取り込みは useCurrentSession 側で行われる。
  const { isPending } = useCurrentSession()
  const [isClockSettled, setIsClockSettled] = useState(false)
  const [hasWaitedTooLong, setHasWaitedTooLong] = useState(false)
  const offsetMs = useServerTimeOffsetMs()
  // clock 確定後の offset を 1 度だけ固定する。 以降の offset 変動では subtree の key が
  // 変わらないため、 定期補正で画面状態が捨てられることはない。
  const [settledOffsetMs, setSettledOffsetMs] = useState<number | null>(null)

  // isPending を直接 render 分岐に使わず state を挟むのは effect の実行順に依存する。
  // useCurrentSession 内の applyServerTime は同じコンポーネントの先行 hook なので
  // この effect より先に走り、 children が最初に render される時点で offset は確定済みになる。
  useEffect(() => {
    if (isPending) return
    setIsClockSettled(true)
  }, [isPending])

  useEffect(() => {
    const timerId = setTimeout(() => {
      setHasWaitedTooLong(true)
    }, MAX_WAIT_MS)
    return () => {
      clearTimeout(timerId)
    }
  }, [])

  useEffect(() => {
    if (!isClockSettled) return
    setSettledOffsetMs((prev) => (prev === null ? offsetMs : prev))
  }, [isClockSettled, offsetMs])

  if (!isClockSettled && !hasWaitedTooLong) {
    // 待ちは通常 session RPC 1 往復。 spinner を出さないのは、 この間に何かを描くと
    // first-paint の見た目が 2 段階になる方が体験として悪いため。
    return null
  }

  // 通常経路では clock 確定後に初回描画するので key は最初から確定値で変わらない。
  // 待ち上限で先に描画した経路のみ、 初期値が実際に変わる大きさの offset が確定した
  // 時点で key が変わって subtree が作り直される (raw な ms を key にすると片道遅延
  // ぶんの非 0 offset で必ず作り直してしまうため、 閾値で 2 値に畳む)。
  const clockKey =
    Math.abs(settledOffsetMs ?? offsetMs) >= REBUILD_THRESHOLD_MS
      ? 'server'
      : 'browser'
  return <Fragment key={clockKey}>{children}</Fragment>
}
